import { type Product } from "@fitness-app/data-models/entities/Product";
import { type ProductStats } from "@fitness-app/data-models/entities/ProductStats";
import { getError } from "@fitness-app/utils";

import { type AppThunk } from "../../../index";
import {
  removeProduct,
  subscribeToProductFailed,
  subscribeToProductStarted,
  subscribeToProductSuccess,
  unsubscribeFromProductDetails,
  updateProductDetails,
  updateProductDetailsStats,
} from "../reducer";
import { fetchProduct } from "./fetchProduct";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToProduct =
  (productId: string): AppThunk =>
  async (dispatch, getState, { subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "product");

    try {
      dispatch(subscribeToProductStarted(productId));

      void dispatch(fetchProduct(productId));

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "product", claimedSlot)) {
        return;
      }

      const channelName = `product-details-${productId}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeProductListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "product", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.productListChannelName = channelName;
      subscriptions.realtimeProductListChannel = channel;

      subscriptions.realtimeProductListChannel
        .on<Product>(
          "postgres_changes",
          { event: "*", schema: "public", table: "product", filter: `id=eq.${productId}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductDetails(payload.new));
            }
            if (payload.eventType === "DELETE" && payload.old.id) {
              dispatch(removeProduct(payload.old.id));
            }
          },
        )
        .on<ProductStats>(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "product_stats", filter: `productId=eq.${productId}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateProductDetailsStats({ product_stats: payload.new, id: payload.new.productId }));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToProductSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromProductDetails());
          }
        });
    } catch (error) {
      dispatch(subscribeToProductFailed(getError(error)));
    }
  };
